import { useMemo } from 'react'
import { Trans } from '@lingui/react/macro'
import { price, PricingType } from '@guestgoodie/pricing'

import { dateFormatter, priceFormatter } from '../utils/helpers'

export type InvoiceItem = {
  name: string
  unitCents: number
  amount: number
  totalCents: number
}

type Props = {
  interval: PricingType
  rooms: number
  taxes: number
}

export function InvoicePreview({ interval, rooms, taxes }: Props) {
  const showVAT = taxes > 0

  const { unitCents, totalCents, unitSumCents, taxesCents, taxesPercent } =
    useMemo(() => {
      const { unitPrice, totalPrice } = price(interval, rooms)

      const multiplier = (() => {
        switch (interval) {
          case PricingType.Monthly:
            return 1
          case PricingType.Yearly:
            return 12
        }
      })()

      const totalCents = totalPrice * multiplier
      const unitCents = unitPrice * multiplier
      const taxesCents = showVAT ? totalCents * taxes : 0

      return {
        taxesPercent: taxes,
        taxesCents: taxesCents,
        totalCents: totalCents + taxesCents,
        unitCents: unitCents,
        unitSumCents: totalCents
      }
    }, [interval, rooms, taxes, showVAT])

  const { start, end } = useMemo(() => {
    const start = new Date()
    const end = new Date()

    if (interval === PricingType.Monthly) end.setMonth(end.getMonth() + 1)
    if (interval === PricingType.Yearly) end.setFullYear(end.getFullYear() + 1)

    return { start, end }
  }, [interval])

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-center px-6">
        <h4 className="text-text text-heading3">
          <Trans>Time Period</Trans>
        </h4>
        <span className="text-text text-regular">
          {dateFormatter.format(start)} - {dateFormatter.format(end)}
        </span>
      </div>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Name</Trans>
            </th>
            <th>
              <Trans>Unit Price</Trans>
            </th>
            <th>
              <Trans>Amount</Trans>
            </th>
            <th>
              <Trans>Price</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Trans>Rooms</Trans>
            </td>
            <td>{priceFormatter.format(unitCents / 100)}</td>
            <td>{rooms}</td>
            <td>{priceFormatter.format(unitSumCents / 100)}</td>
          </tr>
          {showVAT && (
            <tr>
              <td>
                <Trans>Sales Tax</Trans>
              </td>
              <td>{taxesPercent * 100} %</td>
              <td></td>
              <td>{priceFormatter.format(taxesCents / 100)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col w-full gap-px px-8">
          <div className="w-full h-px bg-text rounded" />
          <div className="w-full h-px bg-text rounded" />
        </div>
        <div className="flex flex-col">
          <div className="w-full flex flex-row items-center justify-between px-8">
            <h3 className="text-text text-heading2">
              <Trans>Total</Trans>
            </h3>
            <h3 className="text-text text-heading2">
              {priceFormatter.format(totalCents / 100)}
            </h3>
          </div>
          {!showVAT && (
            <div className="self-end px-8">
              <span className="text-text text-small">
                <Trans>All prices without taxes.</Trans>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
